import { useState, useEffect, useRef } from 'react';
import {
  Flex,
  Box,
  Button,
  Spinner,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogCloseTrigger,
  DialogBackdrop,
  DialogPositioner,
  Portal,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { LuCopy } from 'react-icons/lu';
import { getPublicMenu, duplicateMenu, getTaskStatus, DEFAULT_MENU_THEME, type PublicMenuResponse } from '../api/menus';
import { getMyBusinesses } from '../api/business';
import MenuContent from '../components/MenuContent';
import { useAuth } from '../hooks/useAuth';

const isDarkColor = (hex: string): boolean => {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
};

interface MenuViewProps {
  showDuplicate?: boolean;
}

const MenuView = ({ showDuplicate = false }: MenuViewProps) => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lang, setLang] = useState<string | undefined>(undefined);
  const [defaultLang, setDefaultLang] = useState<string | undefined>(undefined);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const { data: initialMenu } = useQuery<PublicMenuResponse>({
    queryKey: ['publicMenu', slug],
    queryFn: () => getPublicMenu(slug!),
    enabled: !!slug && !defaultLang,
  });

  useEffect(() => {
    if (initialMenu && !defaultLang) {
      const menuDefault = initialMenu.available_languages[0] || initialMenu.language;
      setDefaultLang(menuDefault);
      setLang(menuDefault);
    }
  }, [initialMenu, defaultLang]);

  const { data: menu, isLoading, isError } = useQuery<PublicMenuResponse>({
    queryKey: ['publicMenu', slug, lang],
    queryFn: () => getPublicMenu(slug!, lang),
    enabled: !!slug && !!lang,
    placeholderData: keepPreviousData,
  });

  const handleDuplicateClick = () => {
    if (!user) {
      setShowLoginDialog(true);
    } else {
      setShowConfirmDialog(true);
    }
  };

  const handleConfirmDuplicate = async () => {
    if (!menu) return;
    setShowConfirmDialog(false);
    setIsDuplicating(true);

    try {
      const { task_id } = await duplicateMenu(menu.id);

      pollRef.current = setInterval(async () => {
        try {
          const task = await getTaskStatus(task_id);
          if (task.status === 'SUCCESS') {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setIsDuplicating(false);

            if (task.result?.status === 'completed' && task.result.menu_id) {
              toast.success('Menu duplicated successfully!');
              try {
                const businesses = await getMyBusinesses();
                if (businesses.length > 0) {
                  navigate(`/businesses/${businesses[0].id}/menus/${task.result.menu_id}`);
                }
              } catch {
                navigate('/');
              }
            } else {
              toast.error(task.result?.error || 'Duplication failed');
            }
          } else if (task.status === 'FAILURE') {
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = null;
            setIsDuplicating(false);
            toast.error(task.result?.error || 'Duplication failed');
          }
        } catch {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
          setIsDuplicating(false);
          toast.error('Failed to check duplication status');
        }
      }, 3000);
    } catch (error: any) {
      setIsDuplicating(false);
      toast.error(error.response?.data?.detail || 'Failed to duplicate menu');
    }
  };

  const effectiveTheme = menu?.theme || DEFAULT_MENU_THEME;
  const menuBg = effectiveTheme.background_color;
  const pageBg = isDarkColor(menuBg) ? '#000000' : '#FFFFFF';
  const dark = isDarkColor(menuBg);

  if (!menu || isLoading) {
    return (
      <Flex minHeight="100vh" justifyContent="center" alignItems="center" backgroundColor={pageBg}>
        <Spinner size="xl" color="#999999" />
      </Flex>
    );
  }

  if (isError) {
    return (
      <Flex minHeight="100vh" justifyContent="center" alignItems="center" backgroundColor={pageBg}>
        <Box fontSize="16px" color="#999999">Menu not found</Box>
      </Flex>
    );
  }

  return (
    <Box minHeight="100vh" backgroundColor={pageBg} style={{ colorScheme: dark ? 'dark' : 'light' }}>
      <Box maxWidth="600px" mx="auto" minHeight="100vh">
        <MenuContent
          name={menu.name}
          description={menu.description}
          currency={menu.currency}
          categories={(menu.categories || []).map(cat => ({
            ...cat,
            items: cat.items.map(item => ({
              ...item,
              is_visible: true,
            })),
          }))}
          theme={effectiveTheme}
          storeInfo={menu.store_info || undefined}
          badgeDefinitions={menu.badge_definitions || []}
          headerImageUrl={menu.header_image_url}
          availableLanguages={menu.available_languages}
          currentLanguage={menu.language}
          onLanguageChange={menu.available_languages.length > 1 ? setLang : undefined}
        />
      </Box>

      {showDuplicate && <>
      {/* Floating Duplicate Button */}
      <Box
        as="button"
        onClick={handleDuplicateClick}
        aria-disabled={isDuplicating || undefined}
        position="fixed"
        bottom="24px"
        right="24px"
        display="flex"
        alignItems="center"
        gap="8px"
        px="16px"
        py="10px"
        borderRadius="99px"
        cursor={isDuplicating ? 'wait' : 'pointer'}
        opacity={isDuplicating ? 0.7 : 1}
        backgroundColor={dark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.8)'}
        color={dark ? '#FFFFFF' : '#FFFFFF'}
        fontSize="14px"
        fontWeight="500"
        boxShadow="0 4px 12px rgba(0,0,0,0.3)"
        transition="all 0.2s"
        zIndex="100"
        _hover={{
          backgroundColor: dark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.9)',
          transform: 'scale(1.05)',
        }}
      >
        {isDuplicating ? (
          <Spinner size="sm" color="white" />
        ) : (
          <LuCopy size={16} />
        )}
        {isDuplicating ? 'Duplicating...' : 'Duplicate'}
      </Box>

      {/* Confirm Duplicate Dialog */}
      <DialogRoot open={showConfirmDialog} onOpenChange={(e) => !e.open && setShowConfirmDialog(false)} placement="center">
        <Portal>
          <DialogBackdrop bg="blackAlpha.600" />
          <DialogPositioner>
            <DialogContent
              backgroundColor="neutral.800"
              borderWidth="1px"
              borderColor="neutral.700"
              borderRadius="8px"
              maxW="400px"
              p={{ base: '20px', md: '24px' }}
            >
              <DialogHeader pb="0">
                <DialogTitle fontSize="20px" fontWeight="500" color="neutral.100">
                  Duplicate Menu
                </DialogTitle>
                <DialogCloseTrigger color="neutral.400" />
              </DialogHeader>
              <DialogBody py="16px">
                <Box fontSize="14px" color="neutral.300" lineHeight="150%">
                  This will create a copy of this menu in your account. The duplicate will be set as inactive so you can review it before publishing.
                </Box>
              </DialogBody>
              <DialogFooter gap="12px">
                <Button
                  type="button"
                  onClick={() => setShowConfirmDialog(false)}
                  height="fit-content"
                  py="8px"
                  px="16px"
                  fontWeight="500"
                  fontSize="14px"
                  color="neutral.300"
                  backgroundColor="transparent"
                  borderWidth="1px"
                  borderColor="neutral.600"
                  _hover={{ borderColor: 'neutral.500', backgroundColor: 'neutral.700' }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleConfirmDuplicate}
                  height="fit-content"
                  py="8px"
                  px="16px"
                  fontWeight="500"
                  fontSize="14px"
                  color="neutral.900"
                  backgroundColor="neutral.100"
                  _hover={{ backgroundColor: 'neutral.200' }}
                >
                  Confirm
                </Button>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>

      {/* Login Required Dialog */}
      <DialogRoot open={showLoginDialog} onOpenChange={(e) => !e.open && setShowLoginDialog(false)} placement="center">
        <Portal>
          <DialogBackdrop bg="blackAlpha.600" />
          <DialogPositioner>
            <DialogContent
              backgroundColor="neutral.800"
              borderWidth="1px"
              borderColor="neutral.700"
              borderRadius="8px"
              maxW="400px"
              p={{ base: '20px', md: '24px' }}
            >
              <DialogHeader pb="0">
                <DialogTitle fontSize="20px" fontWeight="500" color="neutral.100">
                  Login Required
                </DialogTitle>
                <DialogCloseTrigger color="neutral.400" />
              </DialogHeader>
              <DialogBody py="16px">
                <Box fontSize="14px" color="neutral.300" lineHeight="150%">
                  You need to be logged in to duplicate menus. Please log in or create an account to continue.
                </Box>
              </DialogBody>
              <DialogFooter gap="12px">
                <Button
                  type="button"
                  onClick={() => setShowLoginDialog(false)}
                  height="fit-content"
                  py="8px"
                  px="16px"
                  fontWeight="500"
                  fontSize="14px"
                  color="neutral.300"
                  backgroundColor="transparent"
                  borderWidth="1px"
                  borderColor="neutral.600"
                  _hover={{ borderColor: 'neutral.500', backgroundColor: 'neutral.700' }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => navigate('/login')}
                  height="fit-content"
                  py="8px"
                  px="16px"
                  fontWeight="500"
                  fontSize="14px"
                  color="neutral.900"
                  backgroundColor="neutral.100"
                  _hover={{ backgroundColor: 'neutral.200' }}
                >
                  Log In
                </Button>
              </DialogFooter>
            </DialogContent>
          </DialogPositioner>
        </Portal>
      </DialogRoot>
      </>}
    </Box>
  );
};

export default MenuView;
